import { useEffect, useRef } from "react";
import { getFirebaseMessaging } from "@/lib/firebase/config";
import { onMessage } from "firebase/messaging";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@a2z/ui";
import { useTranslations } from "@/lib/i18n/intl";
import { useLocale } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal/index";
import { useNavigate } from "react-router-dom";
import { invalidateSellerOrders } from "@/lib/query/core/invalidation";
import { isSellerNativeApp } from "@/lib/native/runtime";
import { getSellerPath } from "@/lib/routing/portal-urls";

type FcmBootstrapProps = {
  // storeId is still needed for the onMessage active-store ref. Token
  // registration now lives in FcmRegistrationProvider.
  storeId: string | undefined;
  storeSlug: string;
};

export function FcmBootstrap({ storeId, storeSlug }: FcmBootstrapProps) {
  const activeStoreIdRef = useRef<string | undefined>(storeId);
  const toast = useToast();
  const t = useTranslations();
  const locale = useLocale();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const storeSlugRef = useRef(storeSlug);

  useEffect(() => {
    storeSlugRef.current = storeSlug;
  }, [storeSlug]);

  // Keep the active storeId in a ref so the onMessage subscription can read the
  // current store without resubscribing the FCM listener on every store switch.
  useEffect(() => {
    activeStoreIdRef.current = storeId;
  }, [storeId]);

  useEffect(() => {
    if (isSellerNativeApp()) return;
    const messaging = getFirebaseMessaging();
    if (!messaging) return;

    const unsubscribe = onMessage(messaging, (payload) => {
      const data = payload.data ?? {};
      if (data.type !== "new_order") return;

      const orderCode = data.orderCode ?? "";
      const orderId = data.orderId;
      const pushStoreId = data.storeId;
      const activeStoreId = activeStoreIdRef.current;

      // A browser may be registered for multiple seller+store contexts, so this
      // push may target a store that is not the currently-active one. A falsy
      // pushStoreId is treated as the active store for backward-compat.
      const isOtherStore = Boolean(pushStoreId) && pushStoreId !== activeStoreId;

      if (isOtherStore) {
        // The seller routes are scoped by the active store's slug; the payload
        // carries only storeId (no slug), so click-through to the foreign order
        // cannot be done correctly here. Show a neutral, non-navigating toast.
        const title = t(keys.notifications.newOrderOtherStoreTitle);
        const body = t(keys.notifications.newOrderOtherStoreBody);
        // Neutral, non-navigating toast: no onClick, so clicking only dismisses
        // and never sends the active-store-scoped view to a foreign order.
        toast.success(title, body, { durationMs: 10000 });
        return;
      }

      // The push targets the currently-active store, so refresh its orders
      // list/status counts in the background — no manual refresh needed.
      void invalidateSellerOrders(queryClient, storeSlugRef.current);

      const title = t(keys.notifications.newOrderTitle, { orderCode });
      const body = t(keys.notifications.newOrderBody);
      toast.success(title, body, {
        durationMs: 10000,
        onClick: orderId ? () => navigate(`${getSellerPath(locale, storeSlugRef.current, "/orders")}?order=${orderId}`) : undefined,
      });
    });

    return () => unsubscribe();
  }, [toast, t, locale, navigate, queryClient]);

  return null;
}
